// src/routes/AppRoutes.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Login from '../pages/Login';
import UpdatePassword from '../pages/UpdatePassword';
import Dashboard from '../pages/Dashboard';
import AdminClientManagement from '../pages/ClientManagement';
import Header from '../components/Header.jsx'; // 🔥 New Header component
import AssignmentPanel from '../pages/AssignmentPanel';
import StatsPage from '../pages/StatsPage';
import BulkUploadPage from '../pages/BulkUploadPage';

// Protected Route Component
const ProtectedRoute = ({ children, user }) => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (user.is_new_user === 0 || user.is_new_user === "0") {
    return <Navigate to="/update-password" replace />;
  }
  
  return children;
};

// Layout Wrapper - WITHOUT Sidebar
const LayoutWrapper = ({ children, user, onLogout }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-50">
      <Header user={user} onLogout={onLogout} />
      <div className="pt-16">
        {children}
      </div>
    </div>
  );
};

// Main App Routes Component
const AppRoutes = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load user data on mount
  useEffect(() => {
    const loadUserData = () => {
      try {
        const storedUser = localStorage.getItem('user');
        const token = localStorage.getItem('token');
        
        if (storedUser && token) {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
        } else {
          setUser(null);
        }
      } catch (err) {
        console.error('Error parsing user:', err);
        localStorage.clear();
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    
    loadUserData();
  }, []);

  const handleLoginSuccess = (userData, token, navList) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    if (navList) {
      localStorage.setItem('nav_list', JSON.stringify(navList));
    }
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.clear();
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-950 flex items-center justify-center">
        <div className="relative">
          <div className="w-20 h-20 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <Login onLoginSuccess={handleLoginSuccess} />
      } />
      
      <Route path="/update-password" element={<UpdatePassword />} />
      
      {/* Dashboard Route */}
      <Route path="/dashboard" element={
        <ProtectedRoute user={user}>
          <LayoutWrapper user={user} onLogout={handleLogout}>
            <Dashboard user={user} />
          </LayoutWrapper>
        </ProtectedRoute>
      } />

      {/* Client Management Route */}
      <Route path="/client-management" element={
        <ProtectedRoute user={user}>
          <LayoutWrapper user={user} onLogout={handleLogout}>
            <AdminClientManagement user={user} />
          </LayoutWrapper>
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin/*" element={
        <ProtectedRoute user={user}>
          {user?.role_code === 'ADMIN' ? (
            <LayoutWrapper user={user} onLogout={handleLogout}>
              <Routes>
                <Route path="dashboard" element={<Dashboard user={user} />} />
                <Route path="clients" element={<AdminClientManagement user={user} />} />
              </Routes>
            </LayoutWrapper>
          ) : (
            <Navigate to="/dashboard" replace />
          )}
        </ProtectedRoute>
      } />

      {/* Client Routes */}
      <Route path="/client/*" element={
        <ProtectedRoute user={user}>
          {user?.role_code === 'CLIENT' ? (
            <LayoutWrapper user={user} onLogout={handleLogout}>
              <Routes>
                <Route path="dashboard" element={<Dashboard user={user} />} />
              </Routes>
            </LayoutWrapper>
          ) : (
            <Navigate to="/dashboard" replace />
          )}
        </ProtectedRoute>
      } />

      {/* Stats Route */}
      <Route path="/stats" element={
        <ProtectedRoute user={user}>
          <LayoutWrapper user={user} onLogout={handleLogout}>
            <StatsPage user={user} />
          </LayoutWrapper>
        </ProtectedRoute>
      } />

      {/* Admin Stats Route */}
      <Route path="/admin/stats" element={
        <ProtectedRoute user={user}>
          {user?.role_code === 'ADMIN' ? (
            <LayoutWrapper user={user} onLogout={handleLogout}>
              <StatsPage user={user} />
            </LayoutWrapper>
          ) : (
            <Navigate to="/dashboard" replace />
          )}
        </ProtectedRoute>
      } />

      {/* Bulk Upload Route */}
      <Route path="/bulkupload" element={
        <ProtectedRoute user={user}>
          <LayoutWrapper user={user} onLogout={handleLogout}>
            <BulkUploadPage user={user} />
          </LayoutWrapper>
        </ProtectedRoute>
      } />

      {/* Assignment Route */}
      <Route path="/assignment" element={
        <ProtectedRoute user={user}>
          {user?.role_code === 'ADMIN' ? (
            <LayoutWrapper user={user} onLogout={handleLogout}>
              <AssignmentPanel />
            </LayoutWrapper>
          ) : (
            <Navigate to="/dashboard" replace />
          )}
        </ProtectedRoute>
      } />

      {/* Default Route */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      {/* 404 Route */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

// Wrap with Router
const AppRoutesWithRouter = () => (
  <Router>
    <AppRoutes />
  </Router>
);

export default AppRoutesWithRouter;